import * as Sentry from '@sentry/react';
import { trackEconomyAction } from 'analytics';
import forge from 'apis/forge';
import Icon, { IconType } from 'components/Icon';
import Modal from 'components/Modal';
import { useState } from 'react';
import * as routes from 'routes';
import { useTheme } from 'styled-components';
import { navigate } from 'utils';
import * as S from './styles';

const DISCORD_URL = 'https://discord.gg/NUQFKesuT8';

const buildPromptFromUrl = (url: string): string => {
	let host = '';
	try {
		host = new URL(url).hostname.toLowerCase();
	} catch {
		// not a parseable URL — fall through to generic prompt
	}

	const isFigma = host.endsWith('figma.com');
	const isGithub = host.endsWith('github.com') || host.endsWith('githubusercontent.com');

	if (isFigma) {
		return `I'd like to start a project from this Figma file: ${url}

Explore it and give me a quick read on what's there. Then suggest 2–3 design improvements we could explore.

If you need me to connect Figma, let me know.`;
	}

	if (isGithub) {
		return `I'd like to start a project based on this GitHub repo: ${url}

Explore it and give me a quick read on what's there. Then suggest 2–3 design improvements we could explore.

If you need me to connect GitHub, let me know.`;
	}

	return `I'd like to start a project inspired by this site: ${url}

Take a look and give me a quick read on what's there. Then suggest 2–3 design improvements we could explore.`;
};

interface CancelRetentionModalProps {
	organizationId?: string;
	manageUrl: string;
	onClose: () => void;
}

const CancelRetentionModal = ({
	organizationId,
	manageUrl,
	onClose,
}: CancelRetentionModalProps) => {
	//----------------------------------------------------------------------
	// HOOKS
	//----------------------------------------------------------------------

	const theme = useTheme();
	const [url, setUrl] = useState('');
	const [isLoading, setIsLoading] = useState(false);

	//----------------------------------------------------------------------
	// HANDLERS
	//----------------------------------------------------------------------

	const onCreateDsClick = () => {
		trackEconomyAction('cancel_modal_create_ds_clicked');
		onClose();
		navigate(routes.createDesignSystem(organizationId));
	};

	const onGoClick = async () => {
		const trimmed = url.trim();
		if (!trimmed || isLoading) return;
		trackEconomyAction('cancel_modal_import_url_clicked');
		setIsLoading(true);
		try {
			const res = await forge.createSceneChatFromPrompt.withToast()({
				prompt: buildPromptFromUrl(trimmed),
				...(organizationId && { organizationId }),
			});
			onClose();
			navigate(routes.scene(res.scene.id, res.scene.title));
		} catch (e) {
			Sentry.captureException(e);
			setIsLoading(false);
		}
	};

	const onUrlKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter') {
			e.preventDefault();
			onGoClick();
		}
	};

	const onDiscordClick = () => {
		trackEconomyAction('cancel_modal_discord_clicked');
		window.open(DISCORD_URL, '_blank', 'noopener,noreferrer');
		onClose();
	};

	const onKeepSubscription = () => {
		trackEconomyAction('cancel_modal_kept_subscription');
		onClose();
	};

	const onConfirmCancel = () => {
		trackEconomyAction('cancel_modal_confirmed_cancel');
		window.open(manageUrl, '_blank', 'noopener,noreferrer');
		onClose();
	};

	//----------------------------------------------------------------------
	// RENDERING
	//----------------------------------------------------------------------

	return (
		<Modal
			title="A few things worth trying before you go..."
			cancelLabel="Cancel Subscription"
			submitLabel="Keep Subscription"
			onCancel={onConfirmCancel}
			onSubmit={onKeepSubscription}
			onClickOutside={(e) => {
				e.preventDefault();
				onClose();
			}}
		>
			<S.Base>
				<S.ActionRow type="button" onClick={onCreateDsClick}>
					<S.IconSlot>
						<Icon type={IconType.PALETTE} width={18} height={18} color={theme.gray[1000]} />
					</S.IconSlot>
					<S.TextBlock>
						<S.Title>Set up your design system</S.Title>
						<S.Description>
							Import tokens from GitHub or Figma, or start one from scratch
						</S.Description>
					</S.TextBlock>
					<S.Chevron>
						<Icon type={IconType.LINE_ARROW_RIGHT} width={14} height={14} color={theme.gray[600]} />
					</S.Chevron>
				</S.ActionRow>

				<S.InputRow>
					<S.IconSlot>
						<Icon type={IconType.LAYOUT} width={18} height={18} color={theme.gray[1000]} />
					</S.IconSlot>
					<S.TextBlock>
						<S.Title>Start a project from a URL</S.Title>
						<S.UrlField>
							<S.UrlInput
								type="url"
								value={url}
								onChange={(e) => setUrl(e.target.value)}
								onKeyDown={onUrlKeyDown}
								placeholder="Paste a Figma, GitHub, or website URL"
								disabled={isLoading}
							/>
							<S.GoButton type="button" onClick={onGoClick} disabled={!url.trim() || isLoading}>
								{isLoading ? 'Creating…' : 'Go'}
							</S.GoButton>
						</S.UrlField>
					</S.TextBlock>
				</S.InputRow>

				<S.ActionRow type="button" onClick={onDiscordClick}>
					<S.IconSlot>
						<Icon type={IconType.CHAT} width={18} height={18} color={theme.gray[1000]} />
					</S.IconSlot>
					<S.TextBlock>
						<S.Title>Join our Discord community</S.Title>
						<S.Description>Tips, support, and inspiration from other designers</S.Description>
					</S.TextBlock>
					<S.Chevron>
						<Icon type={IconType.LINE_ARROW_RIGHT} width={14} height={14} color={theme.gray[600]} />
					</S.Chevron>
				</S.ActionRow>
			</S.Base>
		</Modal>
	);
};

export default CancelRetentionModal;
